<template>
    <div class="hidden bg-white md:flex flex-1 flex-col p-8 rounded-lg relative">
        <template v-if="auction && auction.s == AUCTION_LIVE_STATUS_AUCTIONING && auction.m">
            <div class="flex px-16 pb-4 justify-center items-center text-bold text-lg text-center">{{ auction.m }}</div>
        </template>
        <div v-if="selectedLot" class="flex">
            <div class="flex flex-1 flex-col">
                <div class="uppercase">
                    {{ $t('general.lot') }} {{ selectedLot.no }}
                </div>
                <div class="">
                    <span v-if="selectedLot.a">{{ selectedLot.a }},&nbsp;</span>{{ selectedLot.t }}
                </div>
                <div v-if="selectedLot.emin" class="flex text-sm text-gray-500">
                    <span class="mr-1">{{ $t('general.est') }}: </span>
                    <money-format
                        :value="selectedLot.emin"
                        :locale="locale"
                        currency-code="eur"
                        :subunits-value="false"
                        :hide-subunits="true">
                    </money-format>
                    <span class="mx-1">&ndash;</span>
                    <money-format
                        :value="selectedLot.emax"
                        :locale="locale"
                        currency-code="eur"
                        :subunits-value="false"
                        :hide-subunits="true">
                    </money-format>
                </div>
            </div>
            <div class="flex justify-end text-right">
                <div v-if="selectedLot.s == 0" class="flex flex-col"><!-- pending -->
                    <div>
                        {{ $t('lot.next') }}
                    </div>
                    <div v-if="selectedLot.emin" class="flex flex-no-wrap">
                        <span class="mr-1">Est: </span>
                        <money-format
                            :value="selectedLot.emin"
                            :locale="locale"
                            currency-code="eur"
                            :subunits-value="false"
                            :hide-subunits="true">
                        </money-format>
                        <span class="mx-1">&ndash;</span>
                        <money-format
                            :value="selectedLot.emax"
                            :locale="locale"
                            currency-code="eur"
                            :subunits-value="false"
                            :hide-subunits="true">
                        </money-format>
                    </div>
                </div>
                <div v-if="selectedLot.s == 1" class="flex flex-col"><!-- sold -->
                    <div>
                        {{ $t('lots.sold') }}
                    </div>
                    <div>
                        <money-format
                            :value="selectedLot.sp"
                            :locale="locale"
                            currency-code="eur"
                            :subunits-value="false"
                            :hide-subunits="true">
                        </money-format>
                    </div>
                </div>
                <div v-if="selectedLot.s == 2" class="flex flex-col"><!-- pass -->
                    {{ $t('lots.pass') }}
                </div>
                <div v-if="selectedLot.s == 3" class="flex flex-col"><!-- withdrawn -->
                    {{ $t('lots.withdrawn') }}
                </div>
                <div v-if="selectedLot.s == 4" class="flex flex-col"><!-- withdrawn -->
                    {{ $t('lots.withdrawn') }}
                </div>
                <div v-if="selectedLot.s == 5" class="flex flex-col"><!-- post -->
                    {{ $t('lots.sold_post') }}
                </div>
                <div v-if="selectedLot.s == 6" class="flex flex-col"><!-- withdrawn -->
                    {{ $t('lots.withdrawn') }}
                </div>
                <div v-if="selectedLot.s == 7" class="flex flex-col"><!-- open -->
                    <div>
                        {{ $t('lots.open') }}
                    </div>
                    <div v-if="selectedLot.p > 0">
                        <AnimatedNumber :value="selectedLot.p" :precision="0" /> EUR
                    </div>
                </div>
                <div v-if="selectedLot.s == 8" class="flex flex-col"><!-- recalled -->
                    {{ $t('lots.recalled') }}
                </div>
                <div v-if="selectedLot.s == 31" class="flex flex-col"><!-- withdrawn -->
                    {{ $t('lots.withdrawn') }}
                </div>
            </div>
        </div>

        <div v-else="auctionCurrentLot" class="absolute top-0 left-0 ml-4 mt-4">
            <div class="uppercase flex flex-col justify-center items-center">
                <span class="uppercase mb-1 text-gray-500">Lot</span>
                <span class="text-2xl" style="font-size: 32px;">{{ auctionCurrentLot.no }}</span>
            </div>
        </div>

        <div class="lot-image flex flex-1 flex-col items-center justify-center p-8">
            <transition name="fade" mode="out-in">
                <img :key="currentLot.id" :src="currentLot.img"/>
                <!-- <a target="_blank" :href="currentLot.url">
                    <img :key="currentLot.id" :src="currentLot.img"/>
                </a> -->
            </transition>
            <img v-if="nextLot" :src="nextLot.img" width="1" height="1" class="opacity-0">
            <a target="_blank" :href="currentLot.url" class="uppercase text-sm flex flex-no-wrap items-center text-gray-500 hover:text-gray-700 disabled:text-gray-200 mt-4">
                <svg xmlns="http://www.w3.org/2000/svg" class="h-4 w-4 mr-1" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14" />
                </svg>
                {{ $t('lot.details') }}
            </a>
        </div>

        <div v-if="selectedLot" class="flex items-center justify-center my-4">
            <button @click="backToCurrentAuctionLot" class="pulse w-full inline-flex justify-center rounded-md border border-transparent shadow-sm px-4 py-2 bg-black text-base font-medium text-white hover:bg-gray-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-gray-500 sm:ml-3 sm:w-auto">{{ $t('general.back_to_current') }}</button>
        </div>

        <div class="flex flex-wrap justify-around mt-4">
            <button :disabled="! canRecall" @click="$modal.show('recall-lot')" class="uppercase text-sm flex flex-no-wrap items-center text-gray-500 hover:text-gray-700 disabled:text-gray-200">
                <svg xmlns="http://www.w3.org/2000/svg" class="h-6 w-6 mr-1" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M7 11.5V14m0-2.5v-6a1.5 1.5 0 113 0m-3 6a1.5 1.5 0 00-3 0v2a7.5 7.5 0 0015 0v-5a1.5 1.5 0 00-3 0m-6-3V11m0-5.5v-1a1.5 1.5 0 013 0v1m0 0V11m0-5.5a1.5 1.5 0 013 0v3m0 0V11" />
                </svg>
                {{ $t('lot.recall') }}
            </button>

            <button :disabled="! canAbsenteeBid" @click="$modal.show('absentee-bid')" class="uppercase text-sm flex flex-no-wrap items-center text-gray-500 hover:text-gray-700 disabled:text-gray-200">
                <svg xmlns="http://www.w3.org/2000/svg" class="h-6 w-6 mr-1" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M14.121 15.536c-1.171 1.952-3.07 1.952-4.242 0-1.172-1.953-1.172-5.119 0-7.072 1.171-1.952 3.07-1.952 4.242 0M8 10.5h4m-4 3h4m9-1.5a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
                {{ $t('lot.absentee') }}
            </button>

            <!-- <button :disabled="! canRequestReminder" @click="$modal.show('lot-reminder')" class="uppercase text-sm flex flex-no-wrap items-center text-gray-500 hover:text-gray-700 disabled:text-gray-200">
                <svg xmlns="http://www.w3.org/2000/svg" class="h-6 w-6 mr-1" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M15 17h5l-1.405-1.405A2.032 2.032 0 0118 14.158V11a6.002 6.002 0 00-4-5.659V5a2 2 0 10-4 0v.341C7.67 6.165 6 8.388 6 11v3.159c0 .538-.214 1.055-.595 1.436L4 17h5m6 0v1a3 3 0 11-6 0v-1m6 0H9" />
                </svg>
                {{ $t('lot.reminder') }}
            </button> -->
        </div>

        <AbsenteeBid :lot="currentLot" :auction="auction" />
        <LotRecall :lot="currentLot" :auction="auction" />
        <LotReminder :lot="currentLot" :auction="auction" />

    </div>
</template>

<script>
import { mapGetters, mapActions } from 'vuex'
import { Constants } from '../../constants'
import Paddle from '../global/Paddle'
import DetailsImageItem from '../global/DetailsImageItem'
import AnimatedNumber from '../global/AnimatedNumber'
import AbsenteeBid from '../modals/AbsenteeBid'
import LotRecall from '../modals/LotRecall'
import LotReminder from '../modals/LotReminder'

export default {
    components: {
        Paddle, DetailsImageItem, AnimatedNumber, AbsenteeBid, LotRecall, LotReminder
    },
    data () {
        return {
            ...Constants,
            fullscreen: false,
            details: null,
            loading: false,
            activeTab: 0,
            reminder: {
                sms: true,
                email: false,
                notification: true,
            },
        }
    },
    computed: {
        ...mapGetters({
            currency: 'getCurrency',
            locale: 'getLocale',
            user: 'getUser',
            auction: 'getAuction',
            auctionCurrentLot: 'getLot',
            selectedLot: 'getSelectedLot',
            nextLot: 'getNextLot',
            lots: 'getLots',
            showLots: 'getShowLots',
            recallTimer: 'getRecallTimer',
        }),
        currentLot () {
            if (this.selectedLot) {
                return this.selectedLot
            } else if (this.auctionCurrentLot) {
                return this.auctionCurrentLot
            } else {
                return null
            }
        },
        hasPictures() {
            return this.currentLot && Array.isArray(this.currentLot.g) && this.currentLot.g.length > 0
        },
        canRecall() {
            if(! this.currentLot) return false
            if(! this.user) return false
            if(! this.user.id || this.user.approval_status != 2) return false
            if(this.currentLot.s != 2) return false
            if(this.isEndedRecallWindowLocked) return false
            return true
        },
        canAbsenteeBid() {
            if(! this.currentLot) return false
            if(! this.user) return false
            if(! this.user.id || this.user.approval_status != 2) return false
            if(! [0, 7, 8].includes(this.currentLot.s)) return false
            return true
        },
        canRequestReminder() {
            if(! this.currentLot) return false
            if(! this.user) return false
            if(! this.user.id || this.user.approval_status != 2) return false
            if(! [this.LOT_AUCTION_STATUS_PENDING].includes(this.currentLot.s)) return false
            return true
        },
        isEndedRecallWindowLocked() {
            if(! this.auction) return false
            if(! [4, 5].includes(this.auction.md)) return false
            if(this.auction.s != this.AUCTION_LIVE_STATUS_ENDED) return false

            return this.recallTimer <= 0
        }
    },
    methods: {
        ...mapActions({

        }),
        backToCurrentAuctionLot () {
            this.$store.commit('SET_SELECTED_LOT', null)
            if(! this.auctionCurrentLot) return
            this.$nextTick(() => {
                let el = document.getElementById(`lot-id-${this.auctionCurrentLot.id}`)
                if(el) {
                    el.scrollIntoView({ behavior: 'smooth', block: 'nearest', inline: 'start' })
                }
            });
        },
    },
    mounted () {
        //this.$modal.show('lot-reminder')
    }
}

</script>
